import { halfLifeFactor, TAG_VOTE } from './item-tags';

/**
 * The person shortlist (docs/DESIGN_SYSTEM.md "The person shortlist"): which
 * people and groups a person picker shows while its search is empty, for one
 * viewer. The read gathers the rows (`counterpartRanking`,
 * src/read/counterparts.ts); this decides. Pure.
 *
 * Two signals, both from stored truth and both the viewer's own:
 *
 * - **Shared seats.** Every ref that holds a seat (requester or contributor)
 *   on an item where the viewer holds a seat in person, weighted by how
 *   recently that item moved.
 * - **Picks.** Every ref the viewer named in a seat by their own act (seated,
 *   offered, reassigned to), weighted by how recently they did it.
 *
 * Each weight decays with the tag vote's half-life (docs/FIELDS_AND_TAGS.md
 * §6.4), so the two rankings age alike. What a filter chip held is not a
 * signal: it lives in the URL only, and nothing stores it.
 */

/** Starting values (tuned on real use, as the tag vote's are). */
export const COUNTERPART_RANK = {
  /** One item on which a ref holds a seat beside the viewer. */
  sharedSeat: 1.0,
  /** One act of the viewer's that named a ref in a seat. A choice the viewer
   *  made weighs twice a seat they happen to share. */
  pick: 2.0,
  /** Days for a weight to halve: the tag vote's (`TAG_VOTE.halfLifeDays`). */
  halfLifeDays: TAG_VOTE.halfLifeDays,
  /** How many refs a picker shows with an empty search, after the viewer's
   *  own entry. */
  shortlist: 6,
  /** How many ranked refs reach the pickers. More than the shortlist: each
   *  picker shows only its own candidates (a picker of people skips the
   *  groups, a seat picker skips who already holds the seat). */
  delivered: 24,
  /** The newest items, by when they moved, that the shared-seat signal reads.
   *  Older ones weigh almost nothing after the decay (the tag vote's window,
   *  `TAG_VOTE_WINDOW`, is the precedent). */
  seatWindow: 500,
  /** The newest seat acts of the viewer's that the pick signal reads. */
  pickWindow: 500,
} as const;

/** One item on which the viewer holds a seat in person. */
export interface SharedSeatRow {
  requesters: readonly string[];
  contributors: readonly string[];
  /** When the item last moved (`work_items.updated_at`): the weight's clock,
   *  as it is the tag vote's. */
  movedAt: Date;
}

/** One seat act of the viewer's, as the log holds it. */
export interface SeatActRow {
  action: string;
  previousValue: Readonly<Record<string, unknown>> | null;
  newValue: Readonly<Record<string, unknown>> | null;
  occurredAt: Date;
}

/** What `rankCounterparts` reads. */
export interface CounterpartRows {
  seats: readonly SharedSeatRow[];
  acts: readonly SeatActRow[];
}

/** What a picker reads to build its shortlist: the viewer's own ref (null
 *  when nobody is signed in) and the refs ranked for them, best first. */
export interface Counterparts {
  self: string | null;
  ranked: readonly string[];
}

export const NO_COUNTERPARTS: Counterparts = { self: null, ranked: [] };

const SEAT_KEYS = ['requesters', 'contributors'] as const;

function stringsOf(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

/**
 * The refs one seat act named in a seat. An offer names its holder
 * (`newValue.holderRef`); a seat taken or set names the refs its new set
 * holds that its old one did not (`newValue` and `previousValue` carry the
 * seat's array, shape A). Any other act names nobody.
 */
export function pickedRefs(
  act: Pick<SeatActRow, 'action' | 'previousValue' | 'newValue'>,
): string[] {
  if (act.action === 'seat_offered') {
    const holder = act.newValue?.['holderRef'];
    return typeof holder === 'string' ? [holder] : [];
  }
  if (act.action !== 'seat_taken' && act.action !== 'reassigned') return [];
  const out: string[] = [];
  for (const key of SEAT_KEYS) {
    const before = new Set(stringsOf(act.previousValue?.[key]));
    for (const ref of stringsOf(act.newValue?.[key])) {
      if (!before.has(ref) && !out.includes(ref)) out.push(ref);
    }
  }
  return out;
}

/**
 * Rank the refs the viewer works with, best first. A ref's score is the sum
 * of a shared-seat weight for each item it holds a seat on beside the viewer
 * and a pick weight for each act of the viewer's that named it, each times
 * the half-life factor of its date. The viewer is never ranked (the pickers
 * put the viewer's own entry first by themselves), and neither is a ref the
 * viewer cannot name (`nameOf` answers undefined: a deleted account, or a
 * ref absent from their directory). Ties go by name, then by ref, so the
 * order is stable. Pure.
 */
export function rankCounterparts(
  rows: CounterpartRows,
  opts: {
    viewerRef: string;
    now: Date;
    nameOf: (ref: string) => string | undefined;
    limit?: number;
  },
): string[] {
  const score = new Map<string, number>();
  const add = (ref: string, weight: number) => {
    if (ref === opts.viewerRef) return;
    score.set(ref, (score.get(ref) ?? 0) + weight);
  };
  const decay = (at: Date) => halfLifeFactor(at, opts.now, COUNTERPART_RANK.halfLifeDays);
  for (const seat of rows.seats) {
    const weight = COUNTERPART_RANK.sharedSeat * decay(seat.movedAt);
    for (const ref of new Set([...seat.requesters, ...seat.contributors])) add(ref, weight);
  }
  for (const act of rows.acts) {
    const weight = COUNTERPART_RANK.pick * decay(act.occurredAt);
    for (const ref of pickedRefs(act)) add(ref, weight);
  }
  const named: { ref: string; name: string; score: number }[] = [];
  for (const [ref, s] of score) {
    const name = opts.nameOf(ref);
    if (name !== undefined) named.push({ ref, name, score: s });
  }
  named.sort(
    (a, b) =>
      b.score - a.score ||
      a.name.localeCompare(b.name) ||
      (a.ref < b.ref ? -1 : a.ref > b.ref ? 1 : 0),
  );
  const ranked = named.map((n) => n.ref);
  return opts.limit === undefined ? ranked : ranked.slice(0, opts.limit);
}

/**
 * The candidates a person picker shows while its search is empty: the
 * viewer's own entry first, where the picker offers it and `withSelf` holds,
 * then up to `size` others: the ranked refs among the candidates, best first,
 * then the remaining candidates in the order the caller gave them (the item's
 * audience first on a seat, the name elsewhere), so a viewer with no history
 * still sees a full list. A candidate is matched by its ref (`refOf`). Pure.
 */
export function shortlistOf<T>(
  candidates: readonly T[],
  refOf: (candidate: T) => string,
  counterparts: Counterparts,
  opts: { size?: number; withSelf?: boolean } = {},
): T[] {
  const size = opts.size ?? COUNTERPART_RANK.shortlist;
  const byRef = new Map<string, T>();
  for (const c of candidates) {
    const ref = refOf(c);
    if (!byRef.has(ref)) byRef.set(ref, c);
  }
  const out: T[] = [];
  const taken = new Set<string>();
  const self = counterparts.self;
  if (self !== null && opts.withSelf !== false) {
    const own = byRef.get(self);
    if (own !== undefined) {
      out.push(own);
      taken.add(self);
    }
  }
  const others: T[] = [];
  const take = (ref: string) => {
    if (others.length >= size || taken.has(ref) || ref === self) return;
    const c = byRef.get(ref);
    if (c === undefined) return;
    others.push(c);
    taken.add(ref);
  };
  for (const ref of counterparts.ranked) take(ref);
  for (const c of candidates) take(refOf(c));
  return [...out, ...others];
}
